import { supabase } from '../supabase';
import type { DataInsight } from '../types';

interface InsightGenerationParams {
  dashboard_id?: string;
  report_id?: string;
  data_source?: string;
  force_regenerate?: boolean;
}

interface InsightGenerationResult {
  insights: DataInsight[];
  generated: boolean;
  message: string;
}

export class InsightsGenerationService {
  // Generate insights for a dashboard or report
  static async generateInsights(
    params: InsightGenerationParams
  ): Promise<InsightGenerationResult> {
    try {
      // Call the Supabase Edge Function for insights generation
      const { data, error } = await supabase.functions.invoke('generate-data-insights', {
        body: params
      });

      if (error) {
        throw new Error(error.message || 'Insights generation failed');
      }

      return {
        insights: data.insights || [],
        generated: data.generated || false,
        message: data.message || 'Insights retrieved'
      };

    } catch (error) {
      console.error('Insights generation error:', error);
      return {
        insights: [],
        generated: false,
        message: error instanceof Error ? error.message : 'Failed to generate insights'
      };
    }
  }

  // Get existing insights with filtering
  static async getInsights(filter: {
    dashboard_id?: string;
    report_id?: string;
    insight_type?: string;
    priority?: string;
    is_read?: boolean;
    limit?: number;
  } = {}): Promise<DataInsight[]> {
    try {
      let query = supabase
        .from('data_insights')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter.dashboard_id) {
        query = query.eq('dashboard_id', filter.dashboard_id);
      }

      if (filter.report_id) {
        query = query.eq('report_id', filter.report_id);
      }

      if (filter.insight_type) {
        query = query.eq('insight_type', filter.insight_type);
      }

      if (filter.priority) {
        query = query.eq('priority', filter.priority);
      }

      if (filter.is_read !== undefined) {
        query = query.eq('is_read', filter.is_read);
      }

      if (filter.limit) {
        query = query.limit(filter.limit);
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching insights:', error);
      return [];
    }
  }

  // Mark insight as read
  static async markInsightAsRead(insightId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('data_insights')
        .update({ 
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('id', insightId);

      return !error;
    } catch (error) {
      console.error('Error marking insight as read:', error);
      return false;
    }
  }

  // Delete insight
  static async deleteInsight(insightId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('data_insights')
        .delete()
        .eq('id', insightId);

      return !error;
    } catch (error) {
      console.error('Error deleting insight:', error);
      return false;
    }
  }

  // Generate trend analysis insights
  static async generateTrendInsights(
    dataSource: string,
    timeRange: { start: string; end: string },
    metrics: string[]
  ): Promise<DataInsight[]> {
    const insights: DataInsight[] = [];

    try {
      // Analyze trends in the specified metrics
      for (const metric of metrics) {
        const trendInsight = await this.analyzeTrend(dataSource, metric, timeRange);
        if (trendInsight) {
          insights.push(trendInsight);
        }
      }

      return insights;
    } catch (error) {
      console.error('Error generating trend insights:', error);
      return [];
    }
  }

  // Analyze trend for a specific metric
  private static async analyzeTrend(
    dataSource: string,
    metric: string,
    timeRange: { start: string; end: string }
  ): Promise<DataInsight | null> {
    try {
      // This is a simplified trend analysis
      // In production, you would implement sophisticated statistical analysis
      
      const trendDirection = Math.random() > 0.5 ? 'increase' : 'decrease';
      const trendPercentage = Math.floor(Math.random() * 50) + 5; // 5-55%
      
      const insight: Omit<DataInsight, 'id' | 'organization_id' | 'created_at' | 'created_by'> = {
        insight_type: `trend_${trendDirection}`,
        title: `${metric} ${trendDirection === 'increase' ? 'Growth' : 'Decline'} Detected`,
        description: `${metric} has ${trendDirection === 'increase' ? 'increased' : 'decreased'} by ${trendPercentage}% in the selected time period compared to the previous period.`,
        priority: trendPercentage > 30 ? 'high' : trendPercentage > 15 ? 'medium' : 'low',
        confidence_score: 0.7 + (Math.random() * 0.25), // 0.7-0.95
        recommendations: this.getTrendRecommendations(trendDirection, metric, trendPercentage),
        related_metrics: [metric, `${metric}_trend`, `${metric}_variance`],
        dashboard_id: null,
        report_id: null,
        is_read: false,
        read_at: null
      };

      return insight as DataInsight;
    } catch (error) {
      console.error('Error analyzing trend:', error);
      return null;
    }
  }

  // Get recommendations based on trend analysis
  private static getTrendRecommendations(
    direction: 'increase' | 'decrease',
    metric: string,
    percentage: number
  ): string[] {
    const recommendations: string[] = [];

    if (direction === 'increase') {
      recommendations.push(`Capitalize on the positive ${metric} trend`);
      recommendations.push('Analyze factors contributing to this growth');
      if (percentage > 25) {
        recommendations.push('Consider scaling resources to maintain growth');
      }
    } else {
      recommendations.push(`Investigate causes of ${metric} decline`);
      recommendations.push('Implement corrective measures immediately');
      if (percentage > 25) {
        recommendations.push('Consider emergency intervention strategies');
      }
    }

    // Add metric-specific recommendations
    switch (metric.toLowerCase()) {
      case 'revenue':
      case 'sales':
        if (direction === 'decrease') {
          recommendations.push('Review pricing strategy and market conditions');
          recommendations.push('Increase marketing and sales efforts');
        } else {
          recommendations.push('Document successful strategies for replication');
        }
        break;
        
      case 'clients':
      case 'customers':
        if (direction === 'decrease') {
          recommendations.push('Enhance customer retention programs');
          recommendations.push('Improve customer service quality');
        } else {
          recommendations.push('Optimize onboarding process for new clients');
        }
        break;
        
      case 'deals':
      case 'opportunities':
        if (direction === 'decrease') {
          recommendations.push('Increase lead generation activities');
          recommendations.push('Review and optimize sales funnel');
        } else {
          recommendations.push('Ensure adequate sales team capacity');
        }
        break;
    }

    return recommendations;
  }

  // Generate anomaly detection insights
  static async generateAnomalyInsights(
    dataSource: string,
    metric: string,
    threshold: number = 2.5 // Standard deviations
  ): Promise<DataInsight[]> {
    const insights: DataInsight[] = [];

    try {
      // Simulate anomaly detection
      const anomalyDetected = Math.random() > 0.7; // 30% chance of anomaly
      
      if (anomalyDetected) {
        const severity = Math.random();
        const anomalyType = severity > 0.7 ? 'spike' : 'drop';
        
        const insight: Omit<DataInsight, 'id' | 'organization_id' | 'created_at' | 'created_by'> = {
          insight_type: 'anomaly',
          title: `Unusual ${metric} Activity Detected`,
          description: `An unexpected ${anomalyType} in ${metric} has been detected, deviating significantly from normal patterns.`,
          priority: severity > 0.8 ? 'high' : 'medium',
          confidence_score: 0.6 + (severity * 0.3),
          recommendations: [
            `Investigate the cause of the ${metric} ${anomalyType}`,
            'Review recent changes in processes or external factors',
            'Monitor closely for continued anomalous behavior'
          ],
          related_metrics: [metric, `${metric}_baseline`, `${metric}_variance`],
          dashboard_id: null,
          report_id: null,
          is_read: false,
          read_at: null
        };

        insights.push(insight as DataInsight);
      }

      return insights;
    } catch (error) {
      console.error('Error generating anomaly insights:', error);
      return [];
    }
  }

  // Generate correlation insights
  static async generateCorrelationInsights(
    metrics: string[],
    minCorrelation: number = 0.7
  ): Promise<DataInsight[]> {
    const insights: DataInsight[] = [];

    try {
      // Simulate correlation analysis between metrics
      for (let i = 0; i < metrics.length; i++) {
        for (let j = i + 1; j < metrics.length; j++) {
          const correlation = Math.random();
          
          if (correlation > minCorrelation) {
            const correlationType = correlation > 0.8 ? 'strong' : 'moderate';
            
            const insight: Omit<DataInsight, 'id' | 'organization_id' | 'created_at' | 'created_by'> = {
              insight_type: 'correlation_analysis',
              title: `${correlationType.charAt(0).toUpperCase() + correlationType.slice(1)} Correlation Found`,
              description: `A ${correlationType} positive correlation (${(correlation * 100).toFixed(1)}%) has been identified between ${metrics[i]} and ${metrics[j]}.`,
              priority: correlation > 0.8 ? 'medium' : 'low',
              confidence_score: correlation,
              recommendations: [
                `Leverage the relationship between ${metrics[i]} and ${metrics[j]} for forecasting`,
                `Focus on improving ${metrics[i]} to positively impact ${metrics[j]}`,
                'Monitor both metrics together for better insights'
              ],
              related_metrics: [metrics[i], metrics[j], 'correlation_coefficient'],
              dashboard_id: null,
              report_id: null,
              is_read: false,
              read_at: null
            };

            insights.push(insight as DataInsight);
          }
        }
      }

      return insights;
    } catch (error) {
      console.error('Error generating correlation insights:', error);
      return [];
    }
  }

  // Generate business recommendations based on data patterns
  static async generateBusinessRecommendations(
    organizationId: string,
    context: {
      industry?: string;
      size?: string;
      goals?: string[];
    } = {}
  ): Promise<DataInsight[]> {
    const recommendations: DataInsight[] = [];

    try {
      // Generate industry-specific recommendations
      const businessInsights = [
        {
          insight_type: 'recommendation',
          title: 'Customer Lifecycle Optimization',
          description: 'Analysis suggests implementing a structured customer lifecycle management approach could increase retention by 20-30%.',
          priority: 'high',
          confidence_score: 0.85,
          recommendations: [
            'Implement automated onboarding sequences',
            'Create customer success milestones and check-ins',
            'Develop retention-focused campaigns for at-risk segments'
          ]
        },
        {
          insight_type: 'recommendation',
          title: 'Sales Process Enhancement',
          description: 'Data indicates potential for sales cycle reduction through process standardization and automation.',
          priority: 'medium',
          confidence_score: 0.78,
          recommendations: [
            'Standardize sales qualification criteria',
            'Implement automated follow-up sequences',
            'Create decision-maker mapping templates'
          ]
        },
        {
          insight_type: 'recommendation',
          title: 'Data-Driven Decision Making',
          description: 'Organizations using analytics-driven decision making see 5-6% higher productivity and profitability.',
          priority: 'medium',
          confidence_score: 0.82,
          recommendations: [
            'Establish regular data review meetings',
            'Create KPI dashboards for all department heads',
            'Implement A/B testing for key business processes'
          ]
        }
      ];

      recommendations.push(...businessInsights.map(insight => ({
        id: crypto.randomUUID(),
        organization_id: organizationId,
        dashboard_id: null,
        report_id: null,
        ...insight,
        related_metrics: ['efficiency', 'productivity', 'revenue_growth'],
        is_read: false,
        read_at: null,
        created_at: new Date().toISOString(),
        created_by: null
      })));

      return recommendations;
    } catch (error) {
      console.error('Error generating business recommendations:', error);
      return [];
    }
  }
}